import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";

/* ─────────────────────────────────────────────────────────
   DESIGN SYSTEM CONSTANTS & HARMONIZED STYLES
   ───────────────────────────────────────────────────────── */
const ACCENT = "#3f88a5";
const ACCENT_LIGHT = "#f0f8fa";
const ACCENT_BORDER = "#bfe0eb";

const STATUS_CFG = {
  PENDING: { label: "Pending", dot: "#f59e0b", text: "#92400e", bg: "#fffbeb", border: "#fde68a" },
  APPROVED: { label: "Approved", dot: "#10b981", text: "#065f46", bg: "#ecfdf5", border: "#a7f3d0" },
  REJECTED: { label: "Rejected", dot: "#ef4444", text: "#991b1b", bg: "#fef2f2", border: "#fecaca" },
};

const TYPE_CFG = {
  KPI: { label: "KPI", color: "#6d28d9", bg: "#f5f3ff", border: "#ddd6fe" },
  TAG: { label: "Tag", color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd" },
  UNIT: { label: "Unit", color: "#16a34a", bg: "#e8fdf0", border: "#a7f3d0" },
};

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/* ─────────────────────────────────────────────────────────
   MINI COMPONENTS
   ───────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const c = STATUS_CFG[status] || STATUS_CFG.PENDING;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 20,
      fontSize: 10.5, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", whiteSpace: "nowrap",
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
      {c.label}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const c = TYPE_CFG[type] || { label: type, color: "#374151", bg: "#f9fafb", border: "#e5e7eb" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "3px 8px", borderRadius: 6,
      fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>
      {c.label}
    </span>
  );
};

const DropSelect = ({ value, onChange, children, minWidth = 145 }) => (
  <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: 36, paddingLeft: 12, paddingRight: 32,
        border: "1.5px solid #e2e8f0", borderRadius: 8,
        fontSize: 12.5, fontWeight: 600, color: "#475569", background: "#fff",
        outline: "none", cursor: "pointer", appearance: "none",
        fontFamily: "var(--font-sans, inherit)", minWidth, boxSizing: "border-box",
        transition: "border-color 0.15s ease",
      }}
      onFocus={(e) => { e.target.style.borderColor = ACCENT; }}
      onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; }}
    >
      {children}
    </select>
    <span style={{ position: "absolute", right: 11, pointerEvents: "none", fontSize: 9, color: "#94a3b8" }}>▼</span>
  </div>
);

/* ─────────────────────────────────────────────────────────
   REQUEST CARD ROW
   ───────────────────────────────────────────────────────── */
const RequestCard = ({ item, kpiList = [] }) => {
  const [open, setOpen] = useState(false);
  const cardRef = useRef(null);
  const sc = STATUS_CFG[item.status] || STATUS_CFG.PENDING;

  // Resolve KPI title: prefer backend-joined title, then client-side lookup from kpiList, then fallback
  const resolvedKpiTitle = item.linked_kpi_title
    || (item.kpi_id && kpiList.length > 0
      ? (kpiList.find(k => String(k.kpiId) === String(item.kpi_id))?.title ?? null)
      : null);
  const questionTitleText = resolvedKpiTitle || (item.kpi_id ? `KPI #${item.kpi_id}` : "Not linked");

  return (
    <div
      ref={cardRef}
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#cbd5e1";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(15,23,42,0.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e2e8f0";
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.03)";
      }}
    >
      {/* Top accent status indicator */}
      <div style={{ height: 3.5, background: sc.dot }} />

      {/* Main Row */}
      <div
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex", alignItems: "center",
          padding: "14px 18px", cursor: "pointer",
          userSelect: "none",
        }}
      >
        {/* Toggle Indicator */}
        <div style={{
          width: 24, flexShrink: 0, color: "#94a3b8", fontSize: 13,
          transform: open ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.15s ease",
        }}>
          ▸
        </div>

        {/* Title & Info Subtitle */}
        <div style={{ flex: 1, minWidth: 0, paddingRight: 14 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: "#0f172a",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            fontFamily: "var(--font-sans, inherit)",
            letterSpacing: "-0.01em",
          }}>
            {item.title}
          </div>

          <div style={{
            fontSize: 11.5, color: "#64748b", marginTop: 4,
            display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap",
            fontWeight: 500,
          }}>
            <span>Requested: <strong style={{ color: "#334155" }}>{fmt(item.created_at)}</strong></span>

            {/* Clickable-style beautiful Question Badge Tag */}
            {item.request_type === "UNIT" && (
              <>
                <span style={{ color: "#cbd5e1" }}>·</span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    color: resolvedKpiTitle || item.kpi_id ? "#0369a1" : "#64748b",
                    background: resolvedKpiTitle || item.kpi_id ? "#e0f2fe" : "#f1f5f9",
                    border: `1px solid ${resolvedKpiTitle || item.kpi_id ? "#bae6fd" : "#e2e8f0"}`,
                    padding: "1.5px 7px",
                    borderRadius: 6,
                    fontSize: 10.5,
                    fontWeight: 700,
                    maxWidth: 240,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  title={questionTitleText}
                >
                  🔗 {questionTitleText}
                </span>
              </>
            )}

            {item.request_type !== "UNIT" && (item.kpi_id || resolvedKpiTitle) && (
              <>
                <span style={{ color: "#cbd5e1" }}>·</span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    color: "#6d28d9",
                    background: "#f5f3ff",
                    border: "1px solid #ddd6fe",
                    padding: "1.5px 7px",
                    borderRadius: 6,
                    fontSize: 10.5,
                    fontWeight: 700,
                    maxWidth: 240,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  title={resolvedKpiTitle || `KPI #${item.kpi_id}`}
                >
                  🔗 {resolvedKpiTitle || `KPI #${item.kpi_id}`}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Badges in row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <TypeBadge type={item.request_type} />
          <StatusBadge status={item.status} />
        </div>
      </div>

      {/* Expanded Details Panel */}
      {open && (
        <div style={{
          padding: "16px 20px 20px 42px",
          borderTop: "1px solid #f1f5f9",
          background: "#fafbfc",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Premium Question Card Block */}
            {item.request_type === "UNIT" && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "11px 15px", borderRadius: 10,
                background: resolvedKpiTitle ? "#f0f9ff" : "#f8fafc",
                border: `1px solid ${resolvedKpiTitle ? "#bae6fd" : "#e2e8f0"}`,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                  {resolvedKpiTitle ? "🔗" : "ℹ️"}
                </span>
                <div>
                  <div style={{
                    fontSize: 10, fontWeight: 800,
                    color: resolvedKpiTitle ? "#0369a1" : "#64748b",
                    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3
                  }}>
                    Linked KPI / Question
                  </div>
                  <div style={{
                    fontSize: 13, fontWeight: 700,
                    color: resolvedKpiTitle ? "#0c4a6e" : "#475569",
                    lineHeight: 1.4
                  }}>
                    {resolvedKpiTitle || (item.kpi_id ? `KPI #${item.kpi_id}` : "Not linked to a specific question")}
                  </div>
                </div>
              </div>
            )}

            {/* Non-UNIT linked question */}
            {item.request_type !== "UNIT" && (resolvedKpiTitle || item.kpi_id) && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "11px 15px", borderRadius: 10,
                background: "#f5f3ff", border: "1px solid #ddd6fe",
              }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>🔗</span>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#6d28d9", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>
                    Linked Question / KPI
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#4c1d95", lineHeight: 1.4 }}>
                    {resolvedKpiTitle || `KPI #${item.kpi_id}`}
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            {item.description ? (
              <div>
                <div style={DETAIL_LBL}>Description</div>
                <div style={{
                  fontSize: 13, color: "#334155", lineHeight: 1.6,
                  padding: "10px 14px", background: "#fff",
                  border: "1px solid #e2e8f0", borderRadius: 8,
                  fontWeight: 500,
                }}>
                  {item.description}
                </div>
              </div>
            ) : (
              <div>
                <div style={DETAIL_LBL}>Description</div>
                <div style={{ fontSize: 12.5, color: "#94a3b8", fontStyle: "italic" }}>
                  No description provided.
                </div>
              </div>
            )}

            {/* Info Pillars Grid */}
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
              <InfoPill label="Requested On" value={fmt(item.created_at)} />
              {item.actioned_at && <InfoPill label="Actioned On" value={fmt(item.actioned_at)} />}
              {item.financial_year_id && <InfoPill label="Financial Year" value={item.financial_year_id} />}
            </div>

            {/* Rejection Notification */}
            {item.status === "REJECTED" && item.rejection_reason && (
              <div style={{ padding: "12px 16px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}>
                <div style={{ ...DETAIL_LBL, color: "#991b1b", marginBottom: 4 }}>Rejection Reason</div>
                <div style={{ fontSize: 13, color: "#7f1d1d", lineHeight: 1.5, fontWeight: 600 }}>{item.rejection_reason}</div>
              </div>
            )}

            {/* Approval Notification */}
            {item.status === "APPROVED" && (
              <div style={{ padding: "12px 16px", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8 }}>
                <div style={{ fontSize: 13, color: "#065f46", fontWeight: 700, display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span>✓</span>
                    <span>Approved {resolvedKpiTitle ? `and linked to "${resolvedKpiTitle}"` : ""}</span>
                  </div>
                  {item.request_type === "UNIT" && item.linked_unit_name && (
                    <div style={{ fontSize: 12, color: "#047857", fontWeight: 600 }}>
                      Attached Unit: <strong style={{ color: "#065f46" }}>{item.linked_unit_name}</strong>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const InfoPill = ({ label, value, accent }) => (
  <div>
    <div style={DETAIL_LBL}>{label}</div>
    <div style={{ fontSize: 13, color: accent ? ACCENT : "#334155", fontWeight: 700 }}>{value}</div>
  </div>
);

const DETAIL_LBL = {
  fontSize: 10, fontWeight: 800, color: "#94a3b8",
  textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4,
};

/* ─────────────────────────────────────────────────────────
   SPINNER
   ───────────────────────────────────────────────────────── */
const Spin = ({ size = 22 }) => (
  <>
    <style>{`@keyframes _krspin { to { transform: rotate(360deg); } }`}</style>
    <div style={{ width: size, height: size, border: `${size > 16 ? 3 : 2}px solid #e2e8f0`, borderTopColor: ACCENT, borderRadius: "50%", animation: "_krspin 0.75s linear infinite" }} />
  </>
);

/* ─────────────────────────────────────────────────────────
   STAT CHIP
   ───────────────────────────────────────────────────────── */
const StatChip = ({ label, value, valueColor, bg, border, onClick, active }) => (
  <div
    onClick={onClick}
    style={{
      flex: 1, minWidth: 110,
      background: bg, border: `1.5px solid ${active ? valueColor : border}`,
      borderRadius: 12, padding: "12px 18px",
      cursor: onClick ? "pointer" : "default",
      boxShadow: active ? `0 4px 14px ${valueColor}22` : "0 1px 3px rgba(0,0,0,0.02)",
      transform: active ? "translateY(-1px)" : "translateY(0)",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    }}
  >
    <div style={{ fontSize: 26, fontWeight: 850, color: valueColor, lineHeight: 1, letterSpacing: "-0.04em" }}>{value}</div>
    <div style={{ fontSize: 11, fontWeight: 700, color: valueColor, marginTop: 6, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</div>
  </div>
);

/* ─────────────────────────────────────────────────────────
   ICON BUTTON
   ───────────────────────────────────────────────────────── */
const IconBtn = ({ onClick, disabled, children, title, isClose }) => (
  <button
    onClick={onClick} disabled={disabled} title={title}
    style={{
      width: 34, height: 34, border: "1px solid #e2e8f0",
      borderRadius: 8, background: isClose ? "#f8fafc" : "#fff", color: isClose ? "#64748b" : "#64748b",
      fontSize: isClose ? 14 : 15, cursor: disabled ? "not-allowed" : "pointer",
      display: "flex", alignItems: "center", justifyContent: "center",
      opacity: disabled ? 0.5 : 1, flexShrink: 0,
      transition: "all 0.15s ease",
      fontWeight: isClose ? 700 : 500,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = isClose ? "#ef4444" : "#cbd5e1";
      e.currentTarget.style.background = isClose ? "#fef2f2" : "#f8fafc";
      e.currentTarget.style.color = isClose ? "#ef4444" : "#1e293b";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = "#e2e8f0";
      e.currentTarget.style.background = isClose ? "#f8fafc" : "#fff";
      e.currentTarget.style.color = "#64748b";
    }}
  >
    {children}
  </button>
);

/* ─────────────────────────────────────────────────────────
   MAIN MODAL
   ───────────────────────────────────────────────────────── */
const KpiRequestStatusModal = ({ show, onHide, allowedTypes = ["KPI", "TAG", "UNIT"], kpiList = [] }) => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true); setError(null);
    try {
      const companyId = JSON.parse(localStorage.getItem("currentUser") || "null")?.companyId ?? null;
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/getRequestedKpisStatus`,
        {},
        { companyId },
        "GET",
        false,
        false
      );
      if (isMounted.current) {
        isSuccess
          ? setRequests(Array.isArray(data?.data) ? data.data : [])
          : setError("Failed to load requests.");
      }
    } catch { if (isMounted.current) setError("Network error. Please try again."); }
    finally { if (isMounted.current) setIsLoading(false); }
  }, []);

  useEffect(() => {
    if (show) { fetchRequests(); setSearch(""); setStatusFilter("ALL"); setTypeFilter("ALL"); }
  }, [show, fetchRequests]);

  // Filter requests to show only allowedTypes
  const displayedRequests = useMemo(() => {
    return requests.filter(r => allowedTypes.includes(r.request_type));
  }, [requests, allowedTypes]);

  const counts = useMemo(() => ({
    ALL: displayedRequests.length,
    PENDING: displayedRequests.filter((r) => r.status === "PENDING").length,
    APPROVED: displayedRequests.filter((r) => r.status === "APPROVED").length,
    REJECTED: displayedRequests.filter((r) => r.status === "REJECTED").length,
  }), [displayedRequests]);

  const filtered = useMemo(() => displayedRequests.filter((r) => {
    if (statusFilter !== "ALL" && r.status !== statusFilter) return false;
    if (typeFilter !== "ALL" && r.request_type !== typeFilter) return false;
    if (search && !r.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [displayedRequests, statusFilter, typeFilter, search]);

  const hasFilter = statusFilter !== "ALL" || typeFilter !== "ALL" || !!search;

  if (!show) return null;

  const getTypesLabel = () => {
    const list = allowedTypes.map(t => t.toLowerCase());
    if (list.length === 1) return list[0];
    return list.slice(0, -1).join(", ") + " and " + list[list.length - 1];
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onHide(); }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.45)", backdropFilter: "blur(4px)",
        zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <style>{`
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Panel */}
      <div style={{
        background: "#fff",
        borderRadius: 16, width: "100%", maxWidth: 840, maxHeight: "90vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(15,23,42,0.18), 0 4px 16px rgba(15,23,42,0.06)",
        overflow: "hidden",
        animation: "modalSlideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: "20px 24px 18px",
          borderBottom: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{
              margin: 0, fontSize: 17, fontWeight: 800, color: "#0f172a",
              letterSpacing: "-0.02em", fontFamily: "var(--font-sans, inherit)", textTransform: "capitalize"
            }}>
              {allowedTypes.includes("UNIT") && allowedTypes.length === 1
                ? "Unit Request Status & History"
                : allowedTypes.includes("KPI") && allowedTypes.length === 1
                  ? "KPI Request Status & History"
                  : "KPI / Tag Request Status & History"}
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "#64748b", fontFamily: "var(--font-sans, inherit)", fontWeight: 500 }}>
              Track and check detail links of your submitted {getTypesLabel()} requests
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <IconBtn onClick={fetchRequests} disabled={isLoading} title="Refresh">
              {isLoading ? <Spin size={14} /> : "↻"}
            </IconBtn>
            <IconBtn onClick={onHide} isClose={true}>✕</IconBtn>
          </div>
        </div>

        {/* ── Stat chips (only when data loaded) ── */}
        {!isLoading && !error && displayedRequests.length > 0 && (
          <div style={{
            display: "flex", gap: 12, padding: "14px 24px",
            borderBottom: "1px solid #f1f5f9",
            background: "#f8fafc", flexShrink: 0,
          }}>
            <StatChip label="Total" value={counts.ALL} valueColor="#1e293b" bg="#fff" border="#e2e8f0" />
            <StatChip label="Pending" value={counts.PENDING} valueColor="#b45309" bg="#fffbeb" border="#fde68a"
              active={statusFilter === "PENDING"} onClick={() => setStatusFilter(statusFilter === "PENDING" ? "ALL" : "PENDING")} />
            <StatChip label="Approved" value={counts.APPROVED} valueColor="#047857" bg="#ecfdf5" border="#a7f3d0"
              active={statusFilter === "APPROVED"} onClick={() => setStatusFilter(statusFilter === "APPROVED" ? "ALL" : "APPROVED")} />
            <StatChip label="Rejected" value={counts.REJECTED} valueColor="#be123c" bg="#fef2f2" border="#fecaca"
              active={statusFilter === "REJECTED"} onClick={() => setStatusFilter(statusFilter === "REJECTED" ? "ALL" : "REJECTED")} />
          </div>
        )}

        {/* ── Filter bar ── */}
        <div style={{
          display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
          padding: "12px 24px", borderBottom: "1px solid #f1f5f9",
          background: "#fff", flexShrink: 0,
        }}>
          {/* Search */}
          <div style={{ position: "relative", flex: "1 1 180px", minWidth: 150 }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#94a3b8", pointerEvents: "none" }}>🔍</span>
            <input
              type="text" placeholder="Search requests…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", height: 36, paddingLeft: 34, paddingRight: 12,
                border: "1.5px solid #e2e8f0", borderRadius: 8,
                fontSize: 13, fontFamily: "var(--font-sans, inherit)",
                color: "#1e293b", background: "#f8fafc",
                outline: "none", boxSizing: "border-box",
                transition: "border-color 0.15s ease",
              }}
              onFocus={(e) => { e.target.style.borderColor = ACCENT; }}
              onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; }}
            />
          </div>

          {/* Status dropdown */}
          <DropSelect value={statusFilter} onChange={setStatusFilter} minWidth={158}>
            <option value="ALL">All Status ({counts.ALL})</option>
            <option value="PENDING">Pending ({counts.PENDING})</option>
            <option value="APPROVED">Approved ({counts.APPROVED})</option>
            <option value="REJECTED">Rejected ({counts.REJECTED})</option>
          </DropSelect>

          {/* Type dropdown (only shown if there are multiple allowed types) */}
          {allowedTypes.length > 1 && (
            <DropSelect value={typeFilter} onChange={setTypeFilter} minWidth={128}>
              <option value="ALL">All Types</option>
              {allowedTypes.includes("KPI") && <option value="KPI">KPI</option>}
              {allowedTypes.includes("TAG") && <option value="TAG">Tag</option>}
              {allowedTypes.includes("UNIT") && <option value="UNIT">Unit</option>}
            </DropSelect>
          )}

          {/* Clear */}
          {hasFilter && (
            <button
              onClick={() => { setSearch(""); setStatusFilter("ALL"); setTypeFilter("ALL"); }}
              style={{
                height: 36, padding: "0 14px",
                border: "1.5px solid #e2e8f0", borderRadius: 8,
                background: "#fff", color: "#64748b",
                fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans, inherit)",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = "#1e293b"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#64748b"; }}
            >
              ✕ Clear Filters
            </button>
          )}

          {displayedRequests.length > 0 && (
            <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: "#94a3b8", whiteSpace: "nowrap" }}>
              {filtered.length} of {displayedRequests.length} matching
            </span>
          )}
        </div>

        {/* ── Scrollable list ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 20px", background: "#f8fafc" }}>

          {/* Loading */}
          {isLoading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 14 }}>
              <Spin size={32} />
              <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>Loading requests from server…</span>
            </div>
          )}

          {/* Error */}
          {!isLoading && error && (
            <div style={{ textAlign: "center", padding: "64px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#ef4444", marginBottom: 6 }}>Failed to load</div>
              <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>{error}</div>
              <button
                onClick={fetchRequests}
                style={{ height: 38, padding: "0 22px", border: `1.5px solid ${ACCENT}`, borderRadius: 8, background: "#e8f4f8", color: ACCENT, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
              >
                ↻ Retry Loading
              </button>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && filtered.length === 0 && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
              <span style={{ fontSize: 44, opacity: 0.3 }}>📋</span>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#475569" }}>
                {displayedRequests.length === 0 ? "No requests found" : "No matching requests"}
              </div>
              <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>
                {displayedRequests.length === 0
                  ? `You haven't submitted any ${getTypesLabel()} requests yet.`
                  : "Adjust your search terms or filters."}
              </div>
            </div>
          )}

          {/* ── CARD rows with gap between each ── */}
          {!isLoading && !error && filtered.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {filtered.map((item) => (
                <RequestCard key={item.id} item={item} kpiList={kpiList} />
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {!isLoading && !error && filtered.length > 0 && (
          <div style={{
            padding: "12px 24px",
            borderTop: "1px solid #f1f5f9",
            background: "#fff",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexShrink: 0,
          }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#94a3b8" }}>
              Showing {filtered.length} of {displayedRequests.length} requests
            </span>
            <button
              onClick={onHide}
              style={{
                height: 36, padding: "0 20px",
                border: "1.5px solid #e2e8f0", borderRadius: 8,
                background: "#f8fafc", color: "#475569",
                fontSize: 13, fontWeight: 700, cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = "#0f172a"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.color = "#475569"; }}
            >
              Close View
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default KpiRequestStatusModal;
